import { Show, createEffect, createSignal, mergeProps, on, onCleanup, onMount, useContext } from 'solid-js';
import { AngleUpIcon } from '../../components/icons-library/angle-up';
import { ErrorCatcher } from '../../tools/error-catcher';
import { Heading } from '../heading/heading';
import { StyledRelativeContainer } from '../utility-style-components/positioning';
import { ExpandableProps } from './expandable-container.d';
import { StyledExpandableContainer, StyledExpandableWrapper, StyledExpandableHeadingRowButton, StyledExpandableHeadingRowDiv, StyledChildrenContainer } from './expandable-container.styles';
import { AppContext } from '../../app-context-provider/app-context-provider';
import theme from '../../style/theme';
import { Dynamic, isServer } from 'solid-js/web';
import { StyledHorizontalSpace } from '../utility-style-components/spacing';
import clsx from 'clsx';

const defaultProps = {
    isDropdown: false,
};

const elementOptions = {
    StyledExpandableHeadingRowDiv,
    StyledExpandableHeadingRowButton
};

export const ExpandableContainer = (componentProps: ExpandableProps) => {
    const props = mergeProps(defaultProps, componentProps);

    const { viewport, localize } = useContext(AppContext);

    const headerElementId = Math.random().toString();

    let expandableRef: HTMLDivElement | undefined;
    let escapeListenerAdded = false;
    let outsideClickHandlerAdded = false;

    const [ open, setOpen ] = createSignal(false);
    const [ displayToGrid, setDisplayToGrid ] = createSignal(false);

    const isMobile = () => viewport.width <= theme.breakpoints.MOBILE;

    const openContainer = () => setOpen(true);
    const closeContainer = () => setOpen(false);

    onMount(() => {
        if (props.initialOpen) {
            triggerOpeningAnimation();
        }

        const header = document?.getElementById(headerElementId);
        if (header) {
            header.addEventListener('keydown', handleHeaderKeyPress);
        }
    });

    // This effect allows for the parent to control the open state
    createEffect(
        on(
            [
                () => props.forceTimestamp,
                () => props.forceOpen,
                () => props.forceClose
            ],
            () => {
                if (props.forceOpen && !props.forceClose) openContainer();
                if (props.forceClose && !props.forceOpen) closeContainer();
            },
            { defer: true }
        ),
    );

    // Add/remove escape key listener based on open state
    createEffect(() => {
        if (open()) {
            if (!escapeListenerAdded) {
                window.addEventListener('keydown', handleEscapeKey);
                escapeListenerAdded = true;
            }

            if (!outsideClickHandlerAdded) {
                if (!props.disableOutsideClickToClose && (isMobile() || props.enableOutsideClickToCloseOnDesktop)) {
                    window.addEventListener('click', handleClickOutside);
                    outsideClickHandlerAdded = true;
                }
            }
        } else if (!open()) {
            if (escapeListenerAdded) {
                window.removeEventListener('keydown', handleEscapeKey);
                escapeListenerAdded = false;
            }

            if (outsideClickHandlerAdded) {
                window.removeEventListener('click', handleClickOutside);
                outsideClickHandlerAdded = false;
            }
        }
    });

    onCleanup(() => {
        if (isServer) return;
        
        if (escapeListenerAdded) {
            window.removeEventListener('keydown', handleEscapeKey);
            escapeListenerAdded = false;
        }

        if (outsideClickHandlerAdded) {
            window.removeEventListener('click', handleClickOutside);
            outsideClickHandlerAdded = false;
        }

        const container = document?.getElementById(headerElementId);
        if (container) {
            container.removeEventListener('keydown', handleHeaderKeyPress);
        }
    });

    // Escape key handler to close when focused inside
    const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && open()) {
            if (expandableRef && expandableRef.contains(document.activeElement)) {
                triggerClosingAnimation();
            }
        }
    };

    const handleClickOutside = (event: Event) => {
        if (open() && !expandableRef?.contains(event.target as Node)) {
            triggerClosingAnimation();
        }
    };

    const handleHeaderKeyPress = (e: any) => {
        if (e.type === 'keydown' && e.keyCode === 32) { // 32 is the keycode for spacebar
            e.preventDefault(); // Prevent the default spacebar action (scrolling)
            open() 
                ? triggerClosingAnimation()
                : triggerOpeningAnimation();
        }
    };
    
    /**
     * We do this little dance so that the div that gets expanded can initially be "display: none",
     * so that any links are not tabbable/focusable when the container is closed. But for the animation
     * to work it needs to be set to "display: grid" before we trigger the transition.
     */
    const triggerOpeningAnimation = () => {
        setDisplayToGrid(true);
        setTimeout(() => openContainer(), 0);        
    };

    const triggerClosingAnimation = () => {
        closeContainer();
        setTimeout(() => setDisplayToGrid(false), 200);
    };

    const toggleOpenCloseState = () => {
        if (props.disableClick) {
            return;
        }

        open()
            ? triggerClosingAnimation()
            : triggerOpeningAnimation();
    };



    return (
        <ErrorCatcher componentName="Expandable container">
            <StyledExpandableWrapper 
                isDropdown={props.isDropdown}
                ref={expandableRef}
                shrinkOnTablet={props.shrinkOnTablet}
            >
                <StyledExpandableContainer isOpen={open()} isDropdown={props.isDropdown} withBorder={props.withBorder}>
                    <Dynamic 
                        component={elementOptions[props.disableClick ? 'StyledExpandableHeadingRowDiv' : 'StyledExpandableHeadingRowButton']}
                        ref={props.ref}
                        id={headerElementId}
                        onClick={() => toggleOpenCloseState()}
                        aria-label={localize('expandable-container', 'Expandable container')}
                        backgroundColor={props.backgroundColor}
                        customHeadingRowCss={props.customHeadingRowCss}
                        isOpen={open()}
                        withBorder={props.withBorder}
                        roundCorners={props.roundCorners}
                        disableClick={props.disableClick}
                        withPadding={props.withPadding}
                    >
                        <Heading {...props.headingProps} />
                        <Show when={!props.disableClick}>
                            <StyledHorizontalSpace size={1.5} />

                            <StyledRelativeContainer>
                                <span
                                    class={clsx('block transform transition-transform duration-300 origin-center', {
                                        'rotate-180': !open(),
                                    })}
                                >
                                    <AngleUpIcon
                                        fill={props.chevronColor}
                                        width={props.chevronSize || 1.43}
                                        height={props.chevronSize || 1.43}
                                        ariaLabel={open() ? localize('close-submenu', 'Close Submenu') : localize('open-submenu', 'Open Submenu')}
                                    />
                                </span>
                            </StyledRelativeContainer>
                        </Show>
                    </Dynamic>
                    <StyledChildrenContainer
                        withDividerLine={props.withDividerLine}
                        isOpen={open()}
                        class={clsx('expandable-header', {
                            // transition is defined in expandable-container-transition.css
                            'is-open': open(),
                            'as-grid': displayToGrid(),
                        })}
                    >
                        <div class="expandable-body">
                            {props.children}
                        </div>
                    </StyledChildrenContainer>
                </StyledExpandableContainer>
            </StyledExpandableWrapper>
        </ErrorCatcher>
    );
};
